<template>
  <Menu class="focus:outline-none">
    <MenuItems static class="h-full w-full space-y-1 overflow-auto rounded bg-white">
      <template v-if="!loading">
        <template v-for="section in navSections" :key="section.id">
          <h6
            v-if="section.id && itemsBySection(section.id).length"
            class="px-3 pb-1 pt-3 text-xs font-medium uppercase text-primary-60"
          >
            {{ section.name }}
          </h6>
          <MenuItem
            v-for="item in itemsBySection(section.id)"
            v-slot="{ active }"
            :key="item.name"
            :class="[
              'group flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-0',
            ]"
            :data-intercom-target="`VerticalNavigation-${item.key}`"
            @click="updateSelected(item)"
          >
            <div
              :class="[
                item.key == selected.key ? 'bg-primary-95' : '',
                item.key !== selected.key && active && 'bg-primary-99',
                'text-neutral-0',
              ]"
            >
              <component
                :is="item.icon"
                :class="[
                  item.key == selected.key ? 'text-primary-60' : 'text-neutral-40 group-hover:text-primary-40',
                  '-ml-1 mr-3 h-6 w-6 flex-shrink-0',
                ]"
                aria-hidden="true"
              />
              <span :ref="(el) => assignToRefs(el, item.key)" class="truncate">
                {{ item.name }}
                <SimpleBadge v-if="item.hasNew" class="mx-2" :dot="true" theme="green">{{ t("new") }}</SimpleBadge>
              </span>
              <span
                v-if="item.count !== undefined"
                :class="[
                  item.key == selected.key ? 'bg-white' : 'bg-neutral-99 group-hover:bg-neutral-95',
                  'ml-auto inline-block rounded-full px-3 py-0.5 text-xs text-neutral-0',
                ]"
              >
                {{ item.count }}
              </span>
            </div>
          </MenuItem>
        </template>
      </template>
      <div v-if="loading" class="mb-1 animate-pulse space-y-7 px-3 py-3">
        <div class="flex items-center justify-between">
          <div class="h-3 w-40 rounded-xl bg-neutral-99"></div>
          <div class="h-5 w-7 rounded-xl bg-neutral-99"></div>
        </div>
        <div class="flex items-center justify-between">
          <div class="h-3 w-36 rounded-xl bg-neutral-99"></div>
          <div class="h-5 w-7 rounded-xl bg-neutral-99"></div>
        </div>
        <div class="flex items-center justify-between">
          <div class="h-3 w-28 rounded-xl bg-neutral-99"></div>
          <div class="h-5 w-7 rounded-xl bg-neutral-99"></div>
        </div>
        <div class="flex items-center justify-between">
          <div class="h-3 w-40 rounded-xl bg-neutral-99"></div>
          <div class="h-5 w-7 rounded-xl bg-neutral-99"></div>
        </div>
      </div>
    </MenuItems>
  </Menu>
</template>

<script lang="ts" setup>
import { RenderFunction, computed, ref, watch } from "vue";
import { Menu, MenuItems, MenuItem } from "@headlessui/vue";
import { useI18n } from "vue-i18n";
import { useTooltip } from "@composables/tooltips";

import SimpleBadge from "@atoms/SimpleBadge";
import { nextTick } from "process";

//Types
export interface NavItem {
  key: string;
  name: string;
  icon?: RenderFunction;
  count?: number;
  section?: string;
  hasNew?: boolean;
  tooltip?: string;
}

export interface NavSection {
  id: string;
  name: string;
}

export type NavItemArray = Array<NavItem>;
export type NavSectionArray = Array<NavSection>;

const { t } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    sections?: NavSectionArray;
    items: NavItemArray;
    selected: NavItem;
    loading?: boolean;
  }>(),
  {
    sections: () => [] as NavSectionArray,
    items: () => [] as NavItemArray,
    selected: () => ({}) as NavItem,
  },
);

const itemRefs = ref({});
const assignToRefs = (el, key: string) => (itemRefs.value[key] = el);

watch(props.items, () => {
  nextTick(() => {
    props.items.forEach((i) => {
      if (i.tooltip) useTooltip(itemRefs.value[i.key], ref(i.tooltip), { placement: "right" });
    });
  });
});

//Emits
const emit = defineEmits<{
  (e: "update:selected", selected: NavItem): void;
}>();

const updateSelected = (selected: NavItem) => {
  emit("update:selected", selected);
};

const navSections = computed<NavSectionArray>(() => {
  return [{ id: "", name: "" }, ...props.sections] as NavSectionArray;
});

const itemsBySection = (sectionId: string): NavItemArray => {
  if (!sectionId) return props.items.filter((i) => i.section === undefined) as NavItemArray;
  else return props.items.filter((i) => i.section === sectionId) as NavItemArray;
};
</script>

<i18n lang="jsonc">
{
  "es": {
    "new": "Nuevos",
  },
  "pt": {
    "new": "Novos",
  },
}
</i18n>
